import React, { useState, useEffect } from 'react';
import { familyMembersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  User,
  Baby,
  Heart,
  Calendar,
  Droplet,
  AlertTriangle,
  FileText,
  Loader2,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const RELATIONSHIP_OPTIONS = [
  { value: 'child', label: 'Enfant', icon: Baby },
  { value: 'spouse', label: 'Conjoint(e)', icon: Heart },
  { value: 'parent', label: 'Parent', icon: User },
  { value: 'sibling', label: 'Frère/Sœur', icon: Users },
  { value: 'other', label: 'Autre', icon: User }
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Masculin' },
  { value: 'female', label: 'Féminin' }
];

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const FamilyMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Date dropdown options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);
  
  const [form, setForm] = useState({
    full_name: '',
    relationship: '',
    date_of_birth: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    gender: '',
    blood_type: '',
    allergies: '',
    medical_conditions: '',
    notes: ''
  });

  // Update date_of_birth when components change
  useEffect(() => {
    if (form.birth_day && form.birth_month && form.birth_year) {
      const day = form.birth_day.toString().padStart(2, '0');
      const dateStr = `${form.birth_year}-${form.birth_month}-${day}`;
      if (dateStr !== form.date_of_birth) {
        setForm(prev => ({ ...prev, date_of_birth: dateStr }));
      }
    }
  }, [form.birth_day, form.birth_month, form.birth_year]);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await familyMembersAPI.getAll();
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error loading family members:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const parseDateComponents = (dateStr) => {
    if (!dateStr) return { day: '', month: '', year: '' };
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return {
        year: parts[0],
        month: parts[1],
        day: parseInt(parts[2], 10).toString()
      };
    }
    return { day: '', month: '', year: '' };
  };

  const openAddModal = () => {
    setEditingMember(null);
    setForm({
      full_name: '',
      relationship: '',
      date_of_birth: '',
      birth_day: '',
      birth_month: '',
      birth_year: '',
      gender: '',
      blood_type: '',
      allergies: '',
      medical_conditions: '',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (member) => {
    const dateComponents = parseDateComponents(member.date_of_birth);
    setEditingMember(member);
    setForm({
      full_name: member.full_name || '',
      relationship: member.relationship || '',
      date_of_birth: member.date_of_birth || '',
      birth_day: dateComponents.day,
      birth_month: dateComponents.month,
      birth_year: dateComponents.year,
      gender: member.gender || '',
      blood_type: member.blood_type || '',
      allergies: member.allergies || '',
      medical_conditions: member.medical_conditions || '',
      notes: member.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.full_name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!form.relationship) {
      toast.error('Le lien de parenté est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        full_name: form.full_name.trim(),
        relationship: form.relationship,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        blood_type: form.blood_type || null,
        allergies: form.allergies.trim() || null,
        medical_conditions: form.medical_conditions.trim() || null,
        notes: form.notes.trim() || null
      };

      if (editingMember) {
        await familyMembersAPI.update(editingMember.id, data);
        toast.success('Membre mis à jour');
      } else {
        await familyMembersAPI.create(data);
        toast.success('Membre ajouté');
      }
      
      setShowModal(false);
      loadMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Supprimer ${member.full_name} de votre carnet familial ?`)) return;
    
    setDeleting(member.id);
    try {
      await familyMembersAPI.delete(member.id);
      toast.success('Membre supprimé');
      loadMembers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const getRelationshipIcon = (relationship) => {
    const option = RELATIONSHIP_OPTIONS.find(r => r.value === relationship);
    return option ? option.icon : User;
  };

  const getRelationshipLabel = (relationship) => {
    const option = RELATIONSHIP_OPTIONS.find(r => r.value === relationship);
    return option ? option.label : relationship;
  };

  const getRelationshipColor = (relationship) => {
    switch (relationship) {
      case 'child': return 'bg-blue-100 text-blue-700';
      case 'spouse': return 'bg-pink-100 text-pink-700';
      case 'parent': return 'bg-purple-100 text-purple-700';
      case 'sibling': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="family-members-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Carnet familial
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez les profils santé de votre famille
          </p>
        </div>
        <Button
          onClick={openAddModal}
          className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl"
          data-testid="add-member-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Ajouter un membre
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">Prenez RDV pour vos proches</h3>
            <p className="text-sm text-blue-700 mt-1">
              Ajoutez vos enfants, parents ou conjoint pour prendre des rendez-vous en leur nom et gérer leur dossier médical.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      {members.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucun membre</h3>
            <p className="text-slate-500 mt-2 mb-6">
              Ajoutez vos proches pour gérer leur santé
            </p>
            <Button
              onClick={openAddModal}
              className="bg-[#1976D2] hover:bg-[#1565C0]"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter un membre
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => {
            const RelIcon = getRelationshipIcon(member.relationship);
            
            return (
              <Card 
                key={member.id} 
                className="border-0 shadow-sm hover:shadow-md transition-all"
                data-testid={`member-${member.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getRelationshipColor(member.relationship)}`}>
                        <RelIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{member.full_name}</h3>
                        <Badge className={`text-xs ${getRelationshipColor(member.relationship)}`}>
                          {getRelationshipLabel(member.relationship)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {member.age !== undefined && member.age !== null && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span>{member.age} ans</span>
                      </div>
                    )}
                    {member.gender && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <User className="w-4 h-4" />
                        <span>{member.gender === 'male' ? 'Masculin' : member.gender === 'female' ? 'Féminin' : 'Autre'}</span>
                      </div>
                    )}
                    {member.blood_type && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Droplet className="w-4 h-4 text-red-500" />
                        <span>Groupe {member.blood_type}</span>
                      </div>
                    )}
                    {member.allergies && (
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="truncate">Allergies: {member.allergies}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/search?beneficiary=${member.id}`)}
                      className="flex-1 text-[#1976D2] border-[#1976D2]/30"
                      data-testid={`book-for-${member.id}`}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Prendre RDV
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditModal(member)}
                      className="text-slate-600"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(member)}
                      disabled={deleting === member.id}
                      className="text-red-500 hover:text-red-700"
                    >
                      {deleting === member.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingMember ? (
                <>
                  <Edit className="w-5 h-5 text-[#1976D2]" />
                  Modifier un membre
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-[#E53935]" />
                  Ajouter un membre
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingMember 
                ? 'Modifiez les informations de ce membre de votre famille.'
                : 'Ajoutez un proche pour prendre des rendez-vous en son nom.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Name */}
            <div>
              <Label className="text-slate-700">Nom complet <span className="text-red-500">*</span></Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Ex: Jean Dupont"
                className="mt-1"
                data-testid="member-name"
              />
            </div>

            {/* Relationship */}
            <div>
              <Label className="text-slate-700">Lien de parenté <span className="text-red-500">*</span></Label>
              <Select
                value={form.relationship}
                onValueChange={(value) => setForm(prev => ({ ...prev, relationship: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="member-relationship">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date of Birth */}
            <div>
              <Label className="text-slate-700">Date de naissance</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Select
                  value={form.birth_day?.toString() || ''}
                  onValueChange={(value) => setForm(prev => ({ ...prev, birth_day: value }))}
                >
                  <SelectTrigger className="h-12 min-h-[48px]" data-testid="member-birth-day">
                    <SelectValue placeholder="Jour" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {days.map((day) => (
                      <SelectItem key={day} value={day.toString()} className="h-11">{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={form.birth_month || ''}
                  onValueChange={(value) => setForm(prev => ({ ...prev, birth_month: value }))}
                >
                  <SelectTrigger className="h-12 min-h-[48px]" data-testid="member-birth-month">
                    <SelectValue placeholder="Mois" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="h-11">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={form.birth_year?.toString() || ''}
                  onValueChange={(value) => setForm(prev => ({ ...prev, birth_year: value }))}
                >
                  <SelectTrigger className="h-12 min-h-[48px]" data-testid="member-birth-year">
                    <SelectValue placeholder="Année" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()} className="h-11">{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Gender */}
            <div>
              <Label className="text-slate-700">Sexe</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, gender: 'male' }))}
                  className={`h-10 rounded-xl border-2 font-medium text-sm transition-all ${
                    form.gender === 'male'
                      ? 'border-[#1976D2] bg-[#1976D2]/10 text-[#1976D2]'
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }`}
                  data-testid="member-gender-male"
                >
                  Masculin
                </button>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, gender: 'female' }))}
                  className={`h-10 rounded-xl border-2 font-medium text-sm transition-all ${
                    form.gender === 'female'
                      ? 'border-[#E91E63] bg-[#E91E63]/10 text-[#E91E63]'
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }`}
                  data-testid="member-gender-female"
                >
                  Féminin
                </button>
              </div>
            </div>

            {/* Blood Type */}
            <div>
              <Label className="text-slate-700">Groupe sanguin</Label>
              <Select
                value={form.blood_type}
                onValueChange={(value) => setForm(prev => ({ ...prev, blood_type: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="member-blood">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {BLOOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Allergies */}
            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Allergies connues
              </Label>
              <Textarea
                value={form.allergies}
                onChange={(e) => setForm(prev => ({ ...prev, allergies: e.target.value }))}
                placeholder="Ex: Pénicilline, arachides, pollen..."
                rows={2}
                className="mt-1"
                data-testid="member-allergies"
              />
            </div>

            {/* Medical Conditions */}
            <div>
              <Label className="text-slate-700">Antécédents médicaux</Label>
              <Textarea
                value={form.medical_conditions}
                onChange={(e) => setForm(prev => ({ ...prev, medical_conditions: e.target.value }))}
                placeholder="Ex: Asthme, diabète, hypertension..."
                rows={2}
                className="mt-1"
                data-testid="member-conditions"
              />
            </div>

            {/* Notes */}
            <div>
              <Label className="text-slate-700">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Informations complémentaires..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-[#1976D2] hover:bg-[#1565C0]"
                data-testid="save-member-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  editingMember ? 'Enregistrer' : 'Ajouter'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyMembers;
